import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPatch } from '../api';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Loader2, Search, Download, Receipt, ChevronDown, ChevronRight, ArrowDownRight, ArrowUpRight, Check, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : '—');

const fmtMoney = (v) =>
  typeof v === 'number'
    ? `€${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

const STATUS_COLOR = {
  active: 'bg-emerald-500 text-white',
  trialing: 'bg-sky-600 text-white',
  past_due: 'bg-amber-500 text-white',
  cancelled: 'bg-red-600 text-white',
  canceled: 'bg-red-600 text-white',
};

const buildCsv = (rows) => {
  const header = [
    'Studio',
    'Owner email',
    'Signup date',
    'Plan',
    'Cycle',
    'Plan price (monthly)',
    'Plan price (yearly)',
    'Subscription status',
    'Add-ons',
    'Add-ons annual total (€)',
    'Ledger net (€)',
    'Uninvoiced ledger lines',
  ];
  const lines = [header.join(',')];
  for (const r of rows) {
    const addons = (r.addons || []).map((a) => `${a.name}${a.detail ? ` (${a.detail})` : ''}: €${a.annual_cost_eur}`).join('; ');
    const uninvoiced = (r.ledger_recent || []).filter((e) => !e.invoiced_at).length;
    const cells = [
      r.studio_name,
      r.owner_email || '',
      r.signup_date ? new Date(r.signup_date).toISOString().slice(0, 10) : '',
      r.plan_name || '',
      r.billing_cycle || '',
      r.plan_price_monthly ?? '',
      r.plan_price_yearly ?? '',
      r.subscription_status || '',
      addons,
      r.addons_annual_total_eur ?? 0,
      r.ledger_net_eur ?? 0,
      uninvoiced,
    ];
    lines.push(cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','));
  }
  return lines.join('\n');
};

export const AdminBillingPage = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState({});
  const [uninvoicedOnly, setUninvoicedOnly] = useState(false);
  const [busyEntry, setBusyEntry] = useState(null);

  const toggleRow = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const setEntryOnRow = (rowsState, entryId, updated) =>
    rowsState.map((r) => ({
      ...r,
      ledger_recent: (r.ledger_recent || []).map((e) =>
        e.id === entryId ? { ...e, ...updated } : e,
      ),
    }));

  const markInvoiced = async (entry) => {
    const wasInvoiced = !!entry.invoiced_at;
    let invoiceRef = null;
    if (!wasInvoiced) {
      // Optional reference — Esc/empty submits without one.
      invoiceRef = window.prompt(
        `Mark this ${entry.type} as invoiced?\n\nOptional invoice reference (e.g. INV-2026-0001):`,
        '',
      );
      if (invoiceRef === null) return; // user hit Cancel
    }
    setBusyEntry(entry.id);
    try {
      const updated = await apiPatch('/api/admin/billing/ledger/{entry_id}', {
        params: { entry_id: entry.id },
        body: wasInvoiced
          ? { invoiced: false }
          : { invoiced: true, invoice_ref: invoiceRef || null },
      });
      setRows((prev) => setEntryOnRow(prev, entry.id, updated));
      toast.success(wasInvoiced ? 'Invoiced flag cleared' : 'Marked as invoiced');
    } catch (e) {
      toast.error(formatApiError(e, 'Could not update invoice flag'));
    } finally {
      setBusyEntry(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/api/admin/billing/studios');
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e, 'Could not load billing data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let out = rows;
    if (q) {
      out = out.filter(
        (r) =>
          (r.studio_name || '').toLowerCase().includes(q) ||
          (r.owner_email || '').toLowerCase().includes(q) ||
          (r.plan_name || '').toLowerCase().includes(q),
      );
    }
    if (uninvoicedOnly) {
      // Show rows that have at least one uninvoiced ledger entry.
      out = out.filter((r) =>
        (r.ledger_recent || []).some((e) => !e.invoiced_at),
      );
    }
    return out;
  }, [rows, filter, uninvoicedOnly]);

  const totals = useMemo(() => {
    let monthly = 0;
    let yearly = 0;
    let addons = 0;
    let withAddons = 0;
    for (const r of filtered) {
      if (r.billing_cycle === 'yearly' && r.plan_price_yearly) yearly += r.plan_price_yearly;
      else if (r.plan_price_monthly) monthly += r.plan_price_monthly;
      addons += r.addons_annual_total_eur || 0;
      if ((r.addons || []).length > 0) withAddons += 1;
    }
    return { monthly, yearly, addons, withAddons };
  }, [filtered]);

  const exportCsv = () => {
    const csv = buildCsv(filtered);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `studio-billing-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-billing-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold mb-1 flex items-center gap-2">
              <Receipt className="w-7 h-7" />
              Billing Overview
            </h1>
            <p className="text-muted-foreground">
              All platform billing happens off-app. Use this view to invoice each studio for their plan + add-ons.
            </p>
          </div>
          <Button onClick={exportCsv} disabled={loading || rows.length === 0} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        <div className="grid gap-4 sm:grid-cols-4">
          <SummaryStat label="Studios" value={filtered.length} />
          <SummaryStat label="With add-ons" value={totals.withAddons} />
          <SummaryStat label="Monthly (active)" value={fmtMoney(totals.monthly)} />
          <SummaryStat label="Add-ons (annual)" value={fmtMoney(totals.addons)} />
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle>Studios</CardTitle>
              <CardDescription>{rows.length} total</CardDescription>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Switch
                  id="uninvoiced-toggle"
                  checked={uninvoicedOnly}
                  onCheckedChange={setUninvoicedOnly}
                  data-testid="uninvoiced-toggle"
                />
                <Label htmlFor="uninvoiced-toggle" className="text-sm cursor-pointer whitespace-nowrap">
                  Uninvoiced only
                </Label>
              </div>
              <div className="relative w-full sm:w-72">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, plan…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="pl-9"
                  data-testid="billing-search"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-16 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-16 text-center text-muted-foreground text-sm">
                No matches.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-muted-foreground border-b">
                    <tr>
                      <th className="px-2 py-3 w-8"></th>
                      <th className="px-4 py-3">Studio</th>
                      <th className="px-4 py-3">Owner</th>
                      <th className="px-4 py-3">Signup</th>
                      <th className="px-4 py-3">Plan</th>
                      <th className="px-4 py-3 text-right">Plan price</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Add-ons</th>
                      <th className="px-4 py-3 text-right">Add-ons /yr</th>
                      <th className="px-4 py-3 text-right">Ledger net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => {
                      const cycle = r.billing_cycle === 'yearly' ? 'yr' : 'mo';
                      const price =
                        r.billing_cycle === 'yearly'
                          ? r.plan_price_yearly
                          : r.plan_price_monthly;
                      const statusCls = STATUS_COLOR[r.subscription_status] || 'bg-muted';
                      const isOpen = !!expanded[r.studio_id];
                      const hasLedger = (r.ledger_recent || []).length > 0;
                      const Chevron = isOpen ? ChevronDown : ChevronRight;
                      return (
                        <React.Fragment key={r.studio_id}>
                          <tr className="border-b hover:bg-muted/40" data-testid={`billing-row-${r.studio_id}`}>
                            <td className="px-2 py-3">
                              {hasLedger && (
                                <button
                                  className="text-muted-foreground hover:text-foreground"
                                  onClick={() => toggleRow(r.studio_id)}
                                  data-testid={`expand-${r.studio_id}`}
                                  aria-label="Toggle ledger"
                                >
                                  <Chevron className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                            <td className="px-4 py-3 font-medium">{r.studio_name}</td>
                            <td className="px-4 py-3 text-muted-foreground">{r.owner_email || '—'}</td>
                            <td className="px-4 py-3">{fmtDate(r.signup_date)}</td>
                            <td className="px-4 py-3">{r.plan_name || '—'}</td>
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              {price != null ? `${fmtMoney(price)} / ${cycle}` : '—'}
                            </td>
                            <td className="px-4 py-3">
                              {r.subscription_status ? (
                                <Badge className={statusCls}>{r.subscription_status}</Badge>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {(r.addons || []).length === 0 ? (
                                <span className="text-muted-foreground">—</span>
                              ) : (
                                <div className="space-y-1">
                                  {r.addons.map((a) => (
                                    <div key={a.name} className="text-xs">
                                      <span className="font-medium">{a.name}</span>
                                      {a.detail && (
                                        <span className="font-mono text-muted-foreground"> ({a.detail})</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right font-medium">
                              {r.addons_annual_total_eur > 0
                                ? fmtMoney(r.addons_annual_total_eur)
                                : '—'}
                            </td>
                            <td className={`px-4 py-3 text-right font-semibold ${(r.ledger_net_eur || 0) < 0 ? 'text-emerald-600' : ''}`}>
                              {(r.ledger_net_eur || 0) === 0
                                ? '—'
                                : (r.ledger_net_eur < 0
                                  ? `−${fmtMoney(Math.abs(r.ledger_net_eur))}`
                                  : fmtMoney(r.ledger_net_eur))}
                            </td>
                          </tr>
                          {isOpen && hasLedger && (
                            <tr className="bg-muted/30 border-b" data-testid={`ledger-${r.studio_id}`}>
                              <td colSpan={10} className="px-4 py-3">
                                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                                  Recent charges & credits (5 most recent of all-time ledger)
                                </div>
                                <div className="space-y-1.5">
                                  {(r.ledger_recent || []).map((e) => {
                                    const isInvoiced = !!e.invoiced_at;
                                    const isBusy = busyEntry === e.id;
                                    return (
                                      <div key={e.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`ledger-entry-${e.id}`}>
                                        <div className="flex items-start gap-2 flex-1 min-w-0">
                                          {e.type === 'credit' ? (
                                            <ArrowDownRight className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                                          ) : (
                                            <ArrowUpRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                          )}
                                          <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className={isInvoiced ? 'line-through text-muted-foreground' : ''}>
                                                {e.description}
                                              </span>
                                              {isInvoiced && (
                                                <Badge className="bg-emerald-500 text-white text-xs">
                                                  Invoiced{e.invoice_ref ? ` · ${e.invoice_ref}` : ''}
                                                </Badge>
                                              )}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                              {fmtDate(e.created_at)} · {fmtDate(e.period_from)} – {fmtDate(e.period_to)}
                                              {isInvoiced && (
                                                <span className="ml-2">· invoiced {fmtDate(e.invoiced_at)}</span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <span className={`font-mono font-semibold whitespace-nowrap ${e.type === 'credit' ? 'text-emerald-600' : ''} ${isInvoiced ? 'opacity-50' : ''}`}>
                                          {e.type === 'credit' ? '−' : ''}€{Number(e.amount_eur).toFixed(2)}
                                        </span>
                                        <Button
                                          size="sm"
                                          variant={isInvoiced ? 'ghost' : 'outline'}
                                          onClick={() => markInvoiced(e)}
                                          disabled={isBusy}
                                          data-testid={`mark-invoiced-${e.id}`}
                                          className="whitespace-nowrap"
                                        >
                                          {isBusy ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : isInvoiced ? (
                                            <RotateCcw className="w-3 h-3 mr-1" />
                                          ) : (
                                            <Check className="w-3 h-3 mr-1" />
                                          )}
                                          {isInvoiced ? 'Unmark' : 'Mark invoiced'}
                                        </Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

const SummaryStat = ({ label, value }) => (
  <Card>
    <CardContent className="py-5">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </CardContent>
  </Card>
);

export default AdminBillingPage;
